import { GERMAN_STATE_LABELS } from '#lib/constants/germanStates';

import { companyKindLabel, coversAllCompanyKinds } from './companyKinds';
import { facetValueLabel } from './targetingLabels';

const WEBSITE_FILTER_LABELS = {
	all: 'Alle Firmen',
	with: 'Nur mit Website',
	without: 'Nur ohne Website',
};

const LEGAL_FORM_LABELS = {
	gmbh: 'GmbH',
	ag: 'AG',
	ug: 'UG',
	kg: 'KG',
	ohg: 'OHG',
	eg: 'eG',
	ev: 'e.V.',
	gbr: 'GbR',
	ggmbh: 'gGmbH',
	kgaa: 'KGaA',
	se: 'SE',
	ek: 'Einzelkaufmann',
	ewiv: 'EWIV',
	llp: 'LLP',
	foreign: 'Ausländische Gesellschaft',
	municipal: 'Kommunale Einrichtung',
	unknown: 'Unbekannt',
};

function formatRange(from, to, unit = '') {
	const suffix = unit ? ` ${unit}` : '';
	if (from !== null && from !== undefined && to !== null && to !== undefined) return `${from} – ${to}${suffix}`;
	if (from !== null && from !== undefined) return `ab ${from}${suffix}`;
	if (to !== null && to !== undefined) return `bis ${to}${suffix}`;
	return null;
}

function formatEurRange(min, max) {
	const fmt = (v) => {
		if (v === null || v === undefined) return null;
		if (v >= 1000000) return `${(v / 1000000).toFixed(1)} Mio.`;
		if (v >= 1000) return `${(v / 1000).toFixed(0)}k`;
		return `${v}`;
	};
	const fmtMin = fmt(min);
	const fmtMax = fmt(max);
	if (fmtMin && fmtMax) return `${fmtMin} – ${fmtMax} €`;
	if (fmtMin) return `ab ${fmtMin} €`;
	if (fmtMax) return `bis ${fmtMax} €`;
	return null;
}

function formatLegalForms(forms) {
	if (!forms) return null;
	const arr = Array.isArray(forms) ? forms : [forms];
	if (arr.length === 0) return null;
	const labels = arr.map((f) => LEGAL_FORM_LABELS[f] || f);
	return { label: 'Rechtsform', value: labels, type: 'tags' };
}

const WEBSITE_ENTRIES = [
	{ facet: 'customerTypes', label: 'Kundentyp' },
	{ facet: 'businessModel', label: 'Geschäftsmodell' },
	{ facet: 'offerings', label: 'Leistungen' },
	{ facet: 'specializations', label: 'Spezialisierung' },
	{ facet: 'serviceArea', label: 'Servicegebiet' },
	{ facet: 'tech', label: 'Technik' },
];

const AGE_ENTRIES = [
	{ facet: 'oldestCeoAge', label: 'Alter ältester GF' },
	{ facet: 'youngestCeoAge', label: 'Alter jüngster GF' },
];

const labelledTags = (vocabulary, facet, values) => values.map((value) => facetValueLabel(vocabulary, facet, value));

/**
 * Human-readable entries of the managing-director criteria of an HR query input.
 * @param {Object} management - The stored `management` parameter group
 * @param {Object} [vocabulary] - Targeting vocabulary for value labels; raw values without it
 * @returns {Array<{label: string, value: string|string[]|boolean, type: 'text'|'tags'|'boolean'}>}
 */
function formatManagementInput(management, vocabulary) {
	const entries = [];
	for (const { facet, label } of AGE_ENTRIES) {
		if (management[facet]?.length) {
			entries.push({ label, value: labelledTags(vocabulary, facet, management[facet]), type: 'tags' });
		}
	}
	const ceoCount = formatRange(management.ceoCountMin, management.ceoCountMax);
	if (ceoCount) entries.push({ label: 'Anzahl Geschäftsführer', value: ceoCount, type: 'text' });
	if (management.ceoTenureMin !== null && management.ceoTenureMin !== undefined)
		entries.push({
			label: 'Amtszeit Geschäftsführung',
			value: `ab ${management.ceoTenureMin} Jahren`,
			type: 'text',
		});
	if (management.ownerLedOnly === true) entries.push({ label: 'Nur inhabergeführt', value: true, type: 'boolean' });
	return entries;
}

/**
 * Human-readable entries of the targeting criteria of an HR query input.
 * @param {Object} input - Stored HR query input
 * @param {Object} [vocabulary] - Targeting vocabulary for value labels; raw values without it
 * @returns {Array<{label: string, value: string|string[]|boolean, type: 'text'|'tags'|'boolean'}>}
 */
function formatTargetingInput(input, vocabulary) {
	const entries = [];
	if (input.fulltext?.terms?.length > 0) {
		const mode = input.fulltext.mode === 'any' ? 'einer der Begriffe' : 'alle Begriffe';
		entries.push({
			label: 'Volltext Website',
			value: `${input.fulltext.terms.join(', ')} (${mode})`,
			type: 'text',
		});
	}
	const website = input.website ?? {};
	for (const { facet, label } of WEBSITE_ENTRIES) {
		if (!website[facet]?.length) continue;
		entries.push({ label, value: labelledTags(vocabulary, facet, website[facet]), type: 'tags' });
	}
	if (input.hiring?.active === true) entries.push({ label: 'Stellt ein', value: true, type: 'boolean' });
	if (input.hiring?.occupations?.length) {
		const value = labelledTags(vocabulary, 'hiringOccupations', input.hiring.occupations);
		entries.push({ label: 'Gesuchte Berufsfelder', value, type: 'tags' });
	}
	if (input.discoverySource?.length) {
		const value = labelledTags(vocabulary, 'discoverySource', input.discoverySource);
		entries.push({ label: 'Quelle', value, type: 'tags' });
	}
	if (input.loose === true) entries.push({ label: 'Auch unsichere Zuordnungen', value: true, type: 'boolean' });
	if (input.lookalikeSeeds?.length)
		entries.push({ label: 'Ähnlich wie', value: `${input.lookalikeSeeds.length} Beispielfirmen`, type: 'text' });
	entries.push(...formatManagementInput(input.management ?? {}, vocabulary));
	return entries;
}

/**
 * Converts a search query input object into human-readable entries.
 * Supports various query types (Maps, HR, CSV, Anymail) with type-specific formatting.
 * For unknown query types, all fields are output generically.
 * @param {Object} input - Raw input JSON data of the search query
 * @param {string} queryTypeCode - Query type code (e.g. 'b2bheroLeadSearch', 'b2bheroCommercialRegisterSearch', 'csvImport')
 * @param {Object} [vocabulary] - Targeting vocabulary for the labels of HR targeting values
 * @returns {Array<{label: string, value: string|string[]|boolean, type: 'text'|'tags'|'boolean'}>}
 */
export function formatQueryInput(input, queryTypeCode, vocabulary) {
	if (!input || typeof input !== 'object') return [];

	const entries = [];

	switch (queryTypeCode) {
		case 'b2bheroLeadSearch': {
			if (input.searchTerms?.length) entries.push({ label: 'Branchen', value: input.searchTerms, type: 'tags' });
			if (input.location) entries.push({ label: 'Standort', value: input.location, type: 'text' });
			if (input.postalCodeFrom && input.postalCodeTo)
				entries.push({
					label: 'PLZ-Bereich',
					value: `${input.postalCodeFrom} – ${input.postalCodeTo}`,
					type: 'text',
				});
			if (input.postalCodes) entries.push({ label: 'PLZ-Liste', value: input.postalCodes, type: 'text' });
			if (input.states?.length)
				entries.push({
					label: 'Bundesländer',
					value: input.states.map((s) => GERMAN_STATE_LABELS[s] || s),
					type: 'tags',
				});
			if (input.radius) entries.push({ label: 'Radius', value: `${input.radius} km`, type: 'text' });
			if (input.maxResults)
				entries.push({ label: 'Max. Ergebnisse', value: String(input.maxResults), type: 'text' });
			if (input.country) entries.push({ label: 'Land', value: input.country, type: 'text' });
			if (WEBSITE_FILTER_LABELS[input.websiteFilter])
				entries.push({ label: 'Website', value: WEBSITE_FILTER_LABELS[input.websiteFilter], type: 'text' });
			if (input.ratingMin || input.ratingMax) {
				const ratingRange = formatRange(input.ratingMin, input.ratingMax, '★');
				if (ratingRange) entries.push({ label: 'Bewertung', value: ratingRange, type: 'text' });
			}
			if (input.minReviewCount)
				entries.push({ label: 'Min. Bewertungen', value: `≥ ${input.minReviewCount}`, type: 'text' });
			if (input.skipClosedPlaces === false)
				entries.push({ label: 'Geschlossene Unternehmen einbezogen', value: true, type: 'boolean' });
			if (input.nameMatch === 'only_includes')
				entries.push({ label: 'Nur Orte mit Suchbegriff im Namen', value: true, type: 'boolean' });
			if (input.excludeKeywords?.length > 0)
				entries.push({ label: 'Ausschlussbegriffe', value: input.excludeKeywords, type: 'tags' });
			break;
		}

		case 'b2bheroCommercialRegisterSearch': {
			// New unified API format + backward compat with old format + agentic search format
			if (input.industrySearchTerms?.length)
				entries.push({ label: 'Branchen', value: input.industrySearchTerms, type: 'tags' });
			else if (input.industryCodes?.length)
				entries.push({ label: 'Branchen', value: input.industryCodes, type: 'tags' });
			else if (input.keyword) entries.push({ label: 'Stichwort', value: input.keyword, type: 'text' });

			const lf = formatLegalForms(input.legalForm || input.companyLegalForm);
			if (lf) entries.push(lf);

			if (input.location) entries.push({ label: 'Standort', value: input.location, type: 'text' });
			if (input.postalCodeFrom && input.postalCodeTo)
				entries.push({
					label: 'PLZ-Bereich',
					value: `${input.postalCodeFrom} – ${input.postalCodeTo}`,
					type: 'text',
				});
			if (input.postalCodes) entries.push({ label: 'PLZ-Liste', value: input.postalCodes, type: 'text' });
			if (input.states?.length)
				entries.push({
					label: 'Bundesländer',
					value: input.states.map((s) => GERMAN_STATE_LABELS[s] || s),
					type: 'tags',
				});
			if (input.country && input.country !== 'Deutschland')
				entries.push({ label: 'Land', value: input.country, type: 'text' });
			if (input.radius) entries.push({ label: 'Radius', value: `${input.radius} km`, type: 'text' });

			// New format (EUR absolute)
			const rev = formatEurRange(input.revenueMin, input.revenueMax);
			// Old format (Mio. EUR)
			const revOld = !rev ? formatEurRange(input.companyMinRevenue, input.companyMaxRevenue) : null;
			if (rev) entries.push({ label: 'Umsatz', value: rev, type: 'text' });
			else if (revOld) entries.push({ label: 'Umsatz', value: revOld, type: 'text' });

			const bal = formatEurRange(input.balanceTotalMin, input.balanceTotalMax);
			const balOld = !bal
				? formatEurRange(input.companyMinBalanceSheetTotal, input.companyMaxBalanceSheetTotal)
				: null;
			if (bal) entries.push({ label: 'Bilanzsumme', value: bal, type: 'text' });
			else if (balOld) entries.push({ label: 'Bilanzsumme', value: balOld, type: 'text' });

			const earn = formatEurRange(input.earningsMin, input.earningsMax);
			if (earn) entries.push({ label: 'Gewinn', value: earn, type: 'text' });

			const emp =
				formatRange(input.employeesMin, input.employeesMax) ||
				formatRange(input.companyMinEmployees, input.companyMaxEmployees);
			if (emp) entries.push({ label: 'Mitarbeiter', value: emp, type: 'text' });

			if (input.maxResults)
				entries.push({ label: 'Max. Ergebnisse', value: String(input.maxResults), type: 'text' });
			if (input.eventType) entries.push({ label: 'Ereignis', value: input.eventType, type: 'text' });
			if (input.includeEstimated === false)
				entries.push({ label: 'Nur bestätigte Werte', value: true, type: 'boolean' });
			if (input.searchKeywords?.length > 0)
				entries.push({ label: 'Stichwörter', value: input.searchKeywords, type: 'tags' });
			if (input.derivedMetrics?.length > 0)
				entries.push({
					label: 'Erweiterte Filter',
					value: `${input.derivedMetrics.length} aktiv`,
					type: 'text',
				});
			if (input.excludeKeywords?.length > 0)
				entries.push({ label: 'Ausschlussbegriffe', value: input.excludeKeywords, type: 'tags' });
			if (input.companyKinds?.length > 0 && !coversAllCompanyKinds(input.companyKinds)) {
				const value = input.companyKinds.map((kind) => companyKindLabel(vocabulary, kind));
				entries.push({ label: 'Firmenart', value, type: 'tags' });
			}
			entries.push(...formatTargetingInput(input, vocabulary));
			break;
		}

		case 'csvImport': {
			entries.push({ label: 'Quelle', value: 'CSV Import', type: 'text' });
			if (input.fileName) entries.push({ label: 'Datei', value: input.fileName, type: 'text' });
			break;
		}

		case 'anymailfinderCompanySearch': {
			if (input.keyword) entries.push({ label: 'Suchbegriff', value: input.keyword, type: 'text' });
			if (input.location) entries.push({ label: 'Standort', value: input.location, type: 'text' });
			if (input.domain) entries.push({ label: 'Domain', value: input.domain, type: 'text' });
			break;
		}

		case 'anymailfinderPeopleSearch':
		case 'anymailfinderDecisionmakerSearch': {
			if (input.keyword) entries.push({ label: 'Suchbegriff', value: input.keyword, type: 'text' });
			if (input.location) entries.push({ label: 'Standort', value: input.location, type: 'text' });
			if (input.jobTitle) entries.push({ label: 'Jobtitel', value: input.jobTitle, type: 'text' });
			if (input.companyName) entries.push({ label: 'Firma', value: input.companyName, type: 'text' });
			break;
		}

		case 'anymailfinderLinkedinSearch': {
			if (input.linkedinUrl) entries.push({ label: 'LinkedIn URL', value: input.linkedinUrl, type: 'text' });
			if (input.keyword) entries.push({ label: 'Suchbegriff', value: input.keyword, type: 'text' });
			break;
		}

		default: {
			for (const [key, value] of Object.entries(input)) {
				if (value === null || value === undefined || value === '') continue;
				if (typeof value === 'boolean') {
					entries.push({ label: key, value, type: 'boolean' });
				} else if (Array.isArray(value)) {
					entries.push({ label: key, value: value.map(String), type: 'tags' });
				} else {
					entries.push({ label: key, value: String(value), type: 'text' });
				}
			}
		}
	}

	return entries;
}

/**
 * Formats search query inputs as plain text for native title attributes.
 * @param {Object} input - Raw input JSON data of the search query
 * @param {string} queryTypeCode - Query type code
 * @param {Object} [vocabulary] - Targeting vocabulary for the labels of HR targeting values
 * @returns {string} Line-by-line formatted text or empty string if no inputs
 */
export function formatQueryInputPlainText(input, queryTypeCode, vocabulary) {
	const entries = formatQueryInput(input, queryTypeCode, vocabulary);
	if (entries.length === 0) return '';

	return entries
		.map((e) => {
			if (e.type === 'boolean') return `${e.label}: ${e.value ? 'Ja' : 'Nein'}`;
			if (e.type === 'tags') return `${e.label}: ${Array.isArray(e.value) ? e.value.join(', ') : e.value}`;
			return `${e.label}: ${e.value}`;
		})
		.join('\n');
}
